// Continue with SocialPulse — OIDC Authorization Code + PKCE (public client, no secret).
// The browser runs the PKCE exchange; the resulting id_token is posted to our backend
// (/api/auth/oidc-session) which verifies it and issues our own app session.

const ISSUER = (process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER || "").replace(/\/$/, "");
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID || "";
const REDIRECT_URI = `${window.location.origin}/auth/socialpulse-callback`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TX_KEY = "sp_oidc_tx";

export const socialpulseConfigured = !!(ISSUER && CLIENT_ID);

function b64url(bytes) {
  let s = "";
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function randStr(n = 32) {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return b64url(a);
}
async function sha256(v) {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v)));
  return b64url(d);
}

async function discover() {
  // Use our backend's discovery proxy (same-origin) so we don't depend on the
  // provider sending CORS headers to this origin for metadata.
  const r = await fetch(`${API}/.well-known/openid-configuration`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error("Could not reach SocialPulse.");
  const m = await r.json();
  for (const f of ["issuer", "authorization_endpoint", "token_endpoint"]) {
    if (!m[f]) throw new Error(`SocialPulse metadata missing ${f}`);
  }
  if (m.issuer !== ISSUER) throw new Error("SocialPulse issuer mismatch");
  return m;
}

export async function startSocialPulseLogin() {
  if (!socialpulseConfigured) throw new Error("SocialPulse SSO is not configured.");
  const meta = await discover();
  const state = randStr(32);
  const nonce = randStr(32);
  const codeVerifier = randStr(32);
  const codeChallenge = await sha256(codeVerifier);
  sessionStorage.setItem(TX_KEY, JSON.stringify({ state, nonce, codeVerifier, redirectUri: REDIRECT_URI, createdAt: Date.now() }));
  const params = new URLSearchParams({
    response_type: "code", client_id: CLIENT_ID, redirect_uri: REDIRECT_URI,
    scope: "openid profile email", state, nonce,
    code_challenge: codeChallenge, code_challenge_method: "S256",
  });
  window.location.assign(`${meta.authorization_endpoint}?${params}`);
}

// Runs on /auth/socialpulse-callback. Returns { id_token, nonce } for the backend.
export async function finishSocialPulseLogin() {
  const q = new URLSearchParams(window.location.search);
  const err = q.get("error");
  if (err) throw new Error(`SocialPulse sign-in failed: ${q.get("error_description") || err}`);
  const code = q.get("code");
  const returnedState = q.get("state");
  if (!code || !returnedState) throw new Error("Missing authorization response.");
  const tx = JSON.parse(sessionStorage.getItem(TX_KEY) || "null");
  sessionStorage.removeItem(TX_KEY);
  if (!tx) throw new Error("Your sign-in session expired. Please try again.");
  if (Date.now() - tx.createdAt > 10 * 60 * 1000) throw new Error("Sign-in timed out. Please try again.");
  if (returnedState !== tx.state) throw new Error("Security check failed (state mismatch).");

  const meta = await discover();
  const r = await fetch(meta.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code", client_id: CLIENT_ID, code,
      redirect_uri: tx.redirectUri, code_verifier: tx.codeVerifier,
    }),
  });
  if (!r.ok) throw new Error("SocialPulse token exchange failed.");
  const tokens = await r.json();
  if (!tokens.id_token) throw new Error("SocialPulse did not return an id_token.");
  return { id_token: tokens.id_token, nonce: tx.nonce };
}
